import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaFileInvoiceDollar,
  FaHandshake,
  FaMoneyCheckAlt,
  FaTools,
  FaArrowRight
} from 'react-icons/fa';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './AccountingDashboardPage.scss';

const formatCurrency = (value) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const calculateRentalFeeTotal = (rental) => {
  const rent = Number(rental?.rent || 0);
  const rentalPeriod = Number(rental?.rentalPeriod || 0);
  const extraPeriod = Number(rental?.extraPeriod || 0);
  const extraPeriodMonth = Number(rental?.extraPeriodMonth || 0);
  const extraPeriodRent = Number(rental?.extraPeriodRent || rent);

  const rentWithVat = Math.round(rent * 1.2 * 100) / 100;
  const extraPeriodRentWithVat = Math.round(extraPeriodRent * 1.2 * 100) / 100;

  return (
    Math.round(rentWithVat * rentalPeriod * 100) / 100 +
    Math.round(extraPeriodRentWithVat * extraPeriodMonth * 100) / 100 +
    Math.round((extraPeriodRentWithVat * extraPeriod) / 30 * 100) / 100
  );
};

const calculateOwnerServiceFee = (rental) => {
  const totalFee = calculateRentalFeeTotal(rental);
  const commissionRate = Number(rental?.commissionRate || 0);
  const progressPayment = Math.round(totalFee * (commissionRate / 100) * 100) / 100;
  return Math.round((totalFee - progressPayment) * 100) / 100;
};

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const INVOICED_DRAFT_STORAGE_KEY = 'accountingInvoicedDrafts';
const ACCOUNTING_DASHBOARD_CACHE_KEY = 'accountingDashboardCacheV1';

const readInvoicedDrafts = () => {
  try {
    return JSON.parse(localStorage.getItem(INVOICED_DRAFT_STORAGE_KEY) || '{}');
  } catch (error) {
    console.error('Kesilen fatura kayitlari okunamadi:', error);
    return {};
  }
};

const readAccountingDashboardCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ACCOUNTING_DASHBOARD_CACHE_KEY) || '{}');
  } catch (error) {
    console.error('Muhasebe paneli cache okunamadi:', error);
    return {};
  }
};

const writeAccountingDashboardCache = (value) => {
  try {
    sessionStorage.setItem(ACCOUNTING_DASHBOARD_CACHE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error('Muhasebe paneli cache yazilamadi:', error);
  }
};

const extractRentalIdsFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental') {
    return [];
  }

  if (Array.isArray(record.rentalIds) && record.rentalIds.length > 0) {
    return record.rentalIds
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }

  const draftId = String(record.draftId || '');
  const matched = draftId.match(/^rental-[0-9a-f-]{36}-(.+)$/i);
  if (!matched?.[1]) {
    return [];
  }

  return matched[1]
    .split('-')
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);
};

const extractRentalCompanyIdFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental') {
    return '';
  }

  const draftId = String(record.draftId || '');
  const sourceId = String(record.sourceId || '');
  const companyIdMatch = draftId.match(/^rental-([0-9a-f-]{36})(?:-|$)/i);

  if (companyIdMatch?.[1]) {
    return companyIdMatch[1];
  }

  const sourceCompanyIdMatch = sourceId.match(/^([0-9a-f-]{36})(?:-|$)/i);
  return sourceCompanyIdMatch?.[1] || '';
};

const extractRentalInstallmentsFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental' || !Array.isArray(record.rentalInstallments)) {
    return [];
  }

  return record.rentalInstallments
    .map((item) => ({
      rentalId: Number(item?.rentalId),
      installmentNo: Number(item?.installmentNo)
    }))
    .filter(
      (item) =>
        !Number.isNaN(item.rentalId) &&
        item.rentalId > 0 &&
        !Number.isNaN(item.installmentNo) &&
        item.installmentNo > 0
    );
};

const getRentalInstallmentCount = (rental) =>
  Math.max(Number(rental?.invoiceInstallmentCount || rental?.rentalPeriod || 0), 0);

const getRentalInvoiceStartDate = (rental) => {
  const invoiceStartDate = rental?.firstInvoiceDate ? new Date(rental.firstInvoiceDate) : null;
  if (invoiceStartDate && !Number.isNaN(invoiceStartDate.getTime())) {
    return invoiceStartDate;
  }

  const rentalStartDate = rental?.rentalStartDate ? new Date(rental.rentalStartDate) : null;
  return rentalStartDate && !Number.isNaN(rentalStartDate.getTime()) ? rentalStartDate : null;
};

const getRentalMonthlyAmount = (rental) => {
  return Number(rental?.rent || 0);
};

const getDueRentalInstallmentCount = (rental) => {
  const totalInstallments = getRentalInstallmentCount(rental);
  if (totalInstallments === 0) return 0;

  const rentalInvoiceStartDate = getRentalInvoiceStartDate(rental);
  if (!rentalInvoiceStartDate) {
    return totalInstallments;
  }

  const now = new Date();
  const monthStartNow = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthStartRental = new Date(
    rentalInvoiceStartDate.getFullYear(),
    rentalInvoiceStartDate.getMonth(),
    1
  );

  if (monthStartNow < monthStartRental) {
    return 0;
  }

  const diffInMonths =
    (monthStartNow.getFullYear() - monthStartRental.getFullYear()) * 12 +
    (monthStartNow.getMonth() - monthStartRental.getMonth());

  return Math.min(totalInstallments, diffInMonths + 1);
};

const getNewestTimestamp = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const parsed = new Date(value).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
};

const AccountingDashboardPage = () => {
  const navigate = useNavigate();
  const initialCache = readAccountingDashboardCache();
  const [userRole, setUserRole] = useState('');
  const [companies, setCompanies] = useState(() => initialCache.companies || []);
  const [rentalReports, setRentalReports] = useState([]);
  const [rawApprovedRentals, setRawApprovedRentals] = useState(
    () => initialCache.rawApprovedRentals || []
  );
  const [technicalServiceRequests, setTechnicalServiceRequests] = useState(
    () => initialCache.technicalServiceRequests || []
  );
  const [invoicedDrafts, setInvoicedDrafts] = useState(() => readInvoicedDrafts());
  const [loading, setLoading] = useState(
    () =>
      !(
        (initialCache.companies && initialCache.companies.length) ||
        (initialCache.rawApprovedRentals && initialCache.rawApprovedRentals.length) ||
        (initialCache.technicalServiceRequests && initialCache.technicalServiceRequests.length)
      )
  );
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setUserRole(
          decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || ''
        );
      }
    } catch (error) {
      console.error('Muhasebe paneli rol bilgisi okunamadı:', error);
    }
  }, []);

  useEffect(() => {
    const syncInvoicedDrafts = () => setInvoicedDrafts(readInvoicedDrafts());

    window.addEventListener('focus', syncInvoicedDrafts);
    window.addEventListener('storage', syncInvoicedDrafts);

    return () => {
      window.removeEventListener('focus', syncInvoicedDrafts);
      window.removeEventListener('storage', syncInvoicedDrafts);
    };
  }, []);

  useEffect(() => {
    let initialLoadFinished = false;
    const finishInitialLoad = () => {
      if (!initialLoadFinished) {
        initialLoadFinished = true;
        setLoading(false);
      }
    };

    setLoading(true);
    setErrorMessage('');
    setRentalReports([]);

    api
      .get('/Company')
      .then((response) => {
        setCompanies(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Muhasebe paneli firma verileri yüklenemedi:', error);
        setErrorMessage('Muhasebe paneli firma verileri yüklenemedi.');
      })
      .finally(finishInitialLoad);

    api
      .get('/TechnicalService/requests')
      .then((response) => {
        setTechnicalServiceRequests(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Muhasebe paneli teknik servis verileri yüklenemedi:', error);
      })
      .finally(finishInitialLoad);

    api
      .get('/Rental', { params: { serviceStatus: 'Approved' } })
      .then((response) => {
        const fetchedApprovedRentals = response?.data?.data || [];
        setRawApprovedRentals(fetchedApprovedRentals);
      })
      .catch((error) => {
        console.error('Muhasebe paneli kiralama verileri yüklenemedi:', error);
      })
      .finally(finishInitialLoad);
  }, []);

  useEffect(() => {
    writeAccountingDashboardCache({
      companies,
      rawApprovedRentals,
      technicalServiceRequests
    });
  }, [companies, rawApprovedRentals, technicalServiceRequests]);

  const summary = useMemo(() => {
    const invoicedRentalIds = new Set(
      Object.values(invoicedDrafts).flatMap((record) => extractRentalIdsFromInvoiceRecord(record))
    );
    const fullyInvoicedRentalIds = new Set(
      Object.values(invoicedDrafts)
        .filter(
          (record) =>
            record?.type === 'rental' &&
            !Array.isArray(record?.rentalInstallments) &&
            Array.isArray(record?.rentalIds) &&
            record.rentalIds.length > 0
        )
        .flatMap((record) => extractRentalIdsFromInvoiceRecord(record))
    );
    const invoicedRentalInstallmentsMap = Object.values(invoicedDrafts).reduce((acc, record) => {
      extractRentalInstallmentsFromInvoiceRecord(record).forEach((item) => {
        if (!acc[item.rentalId]) {
          acc[item.rentalId] = new Set();
        }
        acc[item.rentalId].add(item.installmentNo);
      });
      return acc;
    }, {});

    const legacyRentalInvoiceCutoffByCompany = Object.values(invoicedDrafts).reduce(
      (acc, record) => {
        if (record?.type !== 'rental' || (record?.rentalIds || []).length) {
          return acc;
        }

        const companyId = extractRentalCompanyIdFromInvoiceRecord(record);
        const sentAt = record?.sentAt ? new Date(record.sentAt) : null;

        if (!companyId || !sentAt || Number.isNaN(sentAt.getTime())) {
          return acc;
        }

        const currentCutoff = acc[companyId];
        if (!currentCutoff || sentAt > currentCutoff) {
          acc[companyId] = sentAt;
        }

        return acc;
      },
      {}
    );

    const uninvoicedApprovedRentals = rawApprovedRentals.filter((rental) => {
      const rentalId = Number(rental?.id);
      if (fullyInvoicedRentalIds.has(rentalId) || invoicedRentalIds.has(rentalId)) {
        return false;
      }

      const companyId = rental?.rentingCompanyId;
      const legacyCutoff = legacyRentalInvoiceCutoffByCompany[companyId];
      if (!legacyCutoff) {
        return true;
      }

      const parsedRentalStart = rental?.rentalStartDate ? new Date(rental.rentalStartDate) : null;
      if (!parsedRentalStart || Number.isNaN(parsedRentalStart.getTime())) {
        return true;
      }

      return parsedRentalStart > legacyCutoff;
    });

    const pendingRentalInvoiceAmount = uninvoicedApprovedRentals.reduce((sum, rental) => {
      const rentalId = Number(rental?.id);
      const billedInstallments = invoicedRentalInstallmentsMap[rentalId] || new Set();
      const dueInstallmentCount = getDueRentalInstallmentCount(rental);
      const pendingInstallments = Array.from({ length: dueInstallmentCount }, (_, index) => index + 1).filter(
        (installmentNo) => !billedInstallments.has(installmentNo)
      );

      return sum + pendingInstallments.length * getRentalMonthlyAmount(rental);
    }, 0);

    const reportBasedProgress = rentalReports.reduce(
      (sum, report) => sum + Number(report?.totalProgressPayment || 0),
      0
    );
    const fallbackOwnerProgress = uninvoicedApprovedRentals.reduce(
      (sum, rental) => sum + calculateOwnerServiceFee(rental),
      0
    );
    const totalOwnerProgress = Math.max(reportBasedProgress, fallbackOwnerProgress);
    const totalOwnerPaid = rentalReports.reduce(
      (sum, report) => sum + Number(report?.totalNonConstructionPayment || 0),
      0
    );
    const totalOwnerInvoiced = rentalReports.reduce(
      (sum, report) => sum + Number(report?.totalNonConstructionPaymentInvoiceAmount || 0),
      0
    );

    const technicalAccountingRows = technicalServiceRequests
      .map((request) => ({
        ...request,
        normalizedStatus: normalizeStatus(request.status),
        invoiceRecord: request.isInvoiceCut
          ? {
              invoiceNumber: request.invoiceNumber || '',
              ettn: request.invoiceEttn || '',
              sentAt: request.invoiceCutDate || null
            }
          : invoicedDrafts[`technical-${request.id}`] || null
      }))
      .filter((request) => request.normalizedStatus === 7);

    const technicalRevenue = technicalAccountingRows.reduce(
      (sum, request) => sum + Number(request.totalPrice || 0),
      0
    );

    const internalTechnicalRows = technicalAccountingRows.filter(
      (request) => !request.externalMachineName && !request.externalMachineModel
    );
    const externalTechnicalRows = technicalAccountingRows.filter(
      (request) => request.externalMachineName || request.externalMachineModel
    );

    return {
      totalOwnerProgress,
      totalOwnerPaid,
      totalOwnerRemaining: totalOwnerProgress - totalOwnerPaid,
      totalOwnerInvoiced,
      totalOwnerToBeInvoiced: pendingRentalInvoiceAmount,
      totalTechnicalRevenue: technicalRevenue,
      internalTechnicalRevenue: internalTechnicalRows.reduce(
        (sum, request) => sum + Number(request.totalPrice || 0),
        0
      ),
      externalTechnicalRevenue: externalTechnicalRows.reduce(
        (sum, request) => sum + Number(request.totalPrice || 0),
        0
      ),
      technicalAccountingCount: technicalAccountingRows.length
    };
  }, [invoicedDrafts, rentalReports, rawApprovedRentals, technicalServiceRequests]);

  const rentalAccountingRows = useMemo(() => {
    const fullyInvoicedRentalIds = new Set(
      Object.values(invoicedDrafts)
        .filter(
          (record) =>
            record?.type === 'rental' &&
            !Array.isArray(record?.rentalInstallments) &&
            Array.isArray(record?.rentalIds) &&
            record.rentalIds.length > 0
        )
        .flatMap((record) => extractRentalIdsFromInvoiceRecord(record))
    );
    const invoicedRentalInstallmentsMap = Object.values(invoicedDrafts).reduce((acc, record) => {
      extractRentalInstallmentsFromInvoiceRecord(record).forEach((item) => {
        if (!acc[item.rentalId]) {
          acc[item.rentalId] = new Set();
        }
        acc[item.rentalId].add(item.installmentNo);
      });
      return acc;
    }, {});

    return rawApprovedRentals
      .map((rental) => {
        const rentalId = Number(rental?.id);
        const billedInstallments = invoicedRentalInstallmentsMap[rentalId] || new Set();
        const totalInstallmentCount = getRentalInstallmentCount(rental);
        const dueInstallmentCount = getDueRentalInstallmentCount(rental);
        const pendingInstallments = Array.from({ length: dueInstallmentCount }, (_, index) => index + 1).filter(
          (installmentNo) => !billedInstallments.has(installmentNo)
        );

        return {
          ...rental,
          rentalId,
          totalInstallmentCount,
          pendingInstallmentCount: fullyInvoicedRentalIds.has(rentalId) ? 0 : pendingInstallments.length,
          pendingAmount: fullyInvoicedRentalIds.has(rentalId)
            ? 0
            : pendingInstallments.length * getRentalMonthlyAmount(rental)
        };
      })
      .filter((rental) => rental.pendingInstallmentCount > 0)
      .sort(
        (a, b) =>
          getNewestTimestamp(b.createdDate, b.createdAt, b.rentalStartDate, b.rentalEndDate) -
          getNewestTimestamp(a.createdDate, a.createdAt, a.rentalStartDate, a.rentalEndDate)
      )
      .slice(0, 3);
  }, [invoicedDrafts, rawApprovedRentals]);

  const technicalRows = useMemo(() => {
    return technicalServiceRequests
      .map((request) => ({
        ...request,
        normalizedStatus: normalizeStatus(request.status),
        invoiceRecord: request.isInvoiceCut
          ? {
              invoiceNumber: request.invoiceNumber || '',
              ettn: request.invoiceEttn || '',
              sentAt: request.invoiceCutDate || null
            }
          : invoicedDrafts[`technical-${request.id}`] || null
      }))
      .filter((request) => request.normalizedStatus === 7 && !request.invoiceRecord)
      .sort(
        (a, b) =>
          getNewestTimestamp(b.accountingTransferDate, b.updatedDate, b.createdDate, b.requestDate, b.operationDate) -
          getNewestTimestamp(a.accountingTransferDate, a.updatedDate, a.createdDate, a.requestDate, a.operationDate)
      )
      .slice(0, 3);
  }, [invoicedDrafts, technicalServiceRequests]);


  const isReadOnlyOwner = false;

  return (
    <div className="accounting-dashboard-page">
      <Navbar />

      <div className="accounting-dashboard-container">
        <header className="accounting-dashboard-header">
          <div>
            <h1>Muhasebe Paneli</h1>
          </div>
        </header>

        {loading && <div className="accounting-dashboard-loading">Muhasebe paneli yükleniyor...</div>}
        {errorMessage && <div className="accounting-dashboard-alert">{errorMessage}</div>}

        <section className="accounting-summary-grid">
          <article className="summary-card">
            <span>Kiralama Hakedişi</span>
            <strong>{formatCurrency(summary.totalOwnerProgress)}</strong>
          </article>
          <article className="summary-card">
            <span>Yapılan Ödeme</span>
            <strong>{formatCurrency(summary.totalOwnerPaid)}</strong>
          </article>
          <article className="summary-card">
            <span>Kalan Alacak</span>
            <strong>{formatCurrency(summary.totalOwnerRemaining)}</strong>
          </article>
          <article className="summary-card">
            <span>Kesilecek Fatura</span>
            <strong>{formatCurrency(summary.totalOwnerToBeInvoiced)}</strong>
          </article>
          <article className="summary-card accent">
            <span>İç Teknik Servis Geliri</span>
            <strong>{formatCurrency(summary.internalTechnicalRevenue)}</strong>
          </article>
          <article className="summary-card accent">
            <span>Dış Teknik Servis Geliri</span>
            <strong>{formatCurrency(summary.externalTechnicalRevenue)}</strong>
          </article>
        </section>

        <section className="accounting-dashboard-grid">
          <article className="accounting-dashboard-card">
            <div className="accounting-dashboard-card__icon">
              <FaHandshake />
            </div>
            <div className="accounting-dashboard-card__content">
              <h2>Kiralama Gelirleri</h2>
            </div>
            <button
              type="button"
              className="accounting-dashboard-card__action"
              onClick={() => !isReadOnlyOwner && navigate('/investeraccountpage')}
              disabled={isReadOnlyOwner}
            >
              Yatırımcı Muhasebesi
            </button>
          </article>

          <article className="accounting-dashboard-card">
            <div className="accounting-dashboard-card__icon">
              <FaMoneyCheckAlt />
            </div>
            <div className="accounting-dashboard-card__content">
              <h2>Ödeme Planları</h2>
            </div>
            <button
              type="button"
              className="accounting-dashboard-card__action"
              onClick={() => !isReadOnlyOwner && navigate('/paymentplan')}
              disabled={isReadOnlyOwner}
            >
              Ödeme Planı
            </button>
          </article>

          <article className="accounting-dashboard-card">
            <div className="accounting-dashboard-card__icon">
              <FaTools />
            </div>
            <div className="accounting-dashboard-card__content">
              <h2>Teknik Servis Gelirleri</h2>
            </div>
            <button
              type="button"
              className="accounting-dashboard-card__action"
              onClick={() => !isReadOnlyOwner && navigate('/technical-service/archive')}
              disabled={isReadOnlyOwner}
            >
              Teknik Servis Arşivi
            </button>
          </article>

          <article className="accounting-dashboard-card">
            <div className="accounting-dashboard-card__icon">
              <FaFileInvoiceDollar />
            </div>
            <div className="accounting-dashboard-card__content">
              <h2>Fatura İşlemleri</h2>
            </div>
            <button
              type="button"
              className="accounting-dashboard-card__action"
              onClick={() => !isReadOnlyOwner && navigate('/accounting/invoice-drafts')}
              disabled={isReadOnlyOwner}
            >
              Fatura İşlemleri
            </button>
          </article>
        </section>

        <section className="accounting-insight-grid">
          <article className="insight-card">
            <div className="insight-card__header">
              <h3>Muhasebeye Aktarılan Vinç Kiralamalar</h3>
              <button
                type="button"
                className="ghost-link"
                onClick={() => !isReadOnlyOwner && navigate('/rental/archive')}
                disabled={isReadOnlyOwner}
              >
                Arşive Git <FaArrowRight />
              </button>
            </div>
            {rentalAccountingRows.length > 0 ? (
              <div className="insight-list">
                {rentalAccountingRows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className="insight-row"
                    onClick={() => !isReadOnlyOwner && navigate('/rental/archive')}
                    disabled={isReadOnlyOwner}
                  >
                    <div>
                      <strong>{row.towerCraneName || row.towerCraneSerialNumber || `Kiralama #${row.id}`}</strong>
                      <span>{row.rentingCompanyName || row.companyName || row.tenantCompanyName || 'Firma belirtilmedi'}</span>
                    </div>
                    <div className="insight-row__meta">
                      <span>{row.pendingInstallmentCount}/{row.totalInstallmentCount} fatura bekliyor</span>
                      <strong>{formatCurrency(row.pendingAmount)}</strong>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="empty-state">Muhasebeye aktarılmış fatura bekleyen vinç kiralaması bulunmuyor.</p>
            )}
          </article>

          <article className="insight-card">
            <div className="insight-card__header">
              <h3>Muhasebeye Aktarılan Teknik Servisler</h3>
              <button
                type="button"
                className="ghost-link"
                onClick={() => !isReadOnlyOwner && navigate('/technical-service/archive')}
                disabled={isReadOnlyOwner}
              >
                Arşive Git <FaArrowRight />
              </button>
            </div>
            {technicalRows.length > 0 ? (
              <div className="insight-list">
                {technicalRows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className="insight-row"
                    onClick={() => !isReadOnlyOwner && navigate('/technical-service/archive')}
                    disabled={isReadOnlyOwner}
                  >
                    <div>
                      <strong>{row.requestNo || `Talep #${row.id}`}</strong>
                      <span>{row.requestingCompanyName || 'Firma belirtilmedi'}</span>
                    </div>
                    <div className="insight-row__meta">
                      <span>Toplam Tutar</span>
                      <strong>{formatCurrency(row.totalPrice)}</strong>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="empty-state">Muhasebeye aktarılmış teknik servis kaydı bulunmuyor.</p>
            )}
          </article>
        </section>
      </div>
    </div>
  );
};

export default AccountingDashboardPage;

